import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAPI, talentAPI, Talent } from '../services/api';
import { queryKeys } from '../lib/queryKeys';

/**
 * React Query hook for brukerens egne talenter (Profil-siden).
 *
 * Henter brukerens talenter og alle tilgjengelige talenter, og eksponerer
 * add/update/remove som mutasjoner som invaliderer talent-listen for brukeren.
 */
export const useMyTalents = (userId: number | undefined) => {
  const queryClient = useQueryClient();

  const {
    data: userTalentsData,
    isLoading: userTalentsLoading,
  } = useQuery({
    queryKey: queryKeys.users.talents(userId!),
    queryFn: () => userAPI.getUserTalents(userId!).then(res => res.talents),
    enabled: !!userId,
  });

  const {
    data: allTalentsData,
    isLoading: allTalentsLoading,
  } = useQuery({
    queryKey: queryKeys.talents.list(),
    queryFn: () => talentAPI.getAll().then(res => res.talenter),
    enabled: !!userId,
  });

  const invalidate = () => {
    if (userId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.talents(userId) });
    }
  };

  const addMutation = useMutation({
    mutationFn: (talent: Talent) =>
      userAPI.addUserTalent(userId!, { talentId: talent.id, erfaringsnivaa: 'avansert' }),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      talentId,
      field,
      value,
    }: {
      talentId: number;
      field: 'erfaringsnivaa' | 'notater';
      value: any;
    }) => userAPI.updateUserTalent(userId!, talentId, { [field]: value }),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (talentId: number) => userAPI.removeUserTalent(userId!, talentId),
    onSuccess: invalidate,
  });

  return {
    userTalents: userTalentsData || [],
    allTalents: allTalentsData || [],
    loading: userTalentsLoading || allTalentsLoading,
    add: (talent: Talent) => addMutation.mutateAsync(talent),
    update: (talentId: number, field: 'erfaringsnivaa' | 'notater', value: any) =>
      updateMutation.mutateAsync({ talentId, field, value }),
    remove: (talentId: number) => removeMutation.mutateAsync(talentId),
    error:
      (addMutation.error as any) ||
      (updateMutation.error as any) ||
      (removeMutation.error as any) ||
      null,
  };
};
